/**
 * VideoViewProvider - how this viewer is looking at the call (issues #123, #124).
 *
 * It owns the view state (`videoViewReducer`): the corner tiles over the world
 * or the full-screen overlay, and which tile is pinned. It lives next to
 * `VideoProvider` rather than in it, because none of it is about the
 * connection: nothing here reaches the server or LiveKit.
 *
 * Full screen never opens by itself (epic D5). It does close by itself when
 * the call ends underneath it, so the player is never left looking at an
 * empty overlay: the area left, a teleport, a kick, a disconnect that is not
 * being retried, the room gone. A reconnect keeps it open (`isCallLive`).
 * When the call ends the pin goes too, so a later call starts tiled.
 *
 * The provider sits outside LiveKit's `RoomContext`, so it cannot read the
 * tiles itself: the video overlay reports them through `syncTiles`, and the
 * reducer clears a pin whose tile has gone and lets a new screen share pin
 * itself (epic D10).
 *
 * What full screen does to the rest of the screen (the keyboard, the game's
 * rendering, focus) belongs to the overlay component, not to this state: it
 * is tied to the overlay being mounted, so a crashed or unmounted overlay can
 * never leave the world frozen underneath.
 */

import { useCallback, useEffect, useMemo, useReducer, useRef, type ReactNode } from 'react';
import { useVideoCall } from '../hooks/useVideoCall.js';
import { useGameManager } from '../hooks/useGameManager';
import { VideoViewContext, type VideoViewContextValue } from './videoViewContext';
import { initialVideoViewState, isCallLive, shareToOffer, videoViewReducer } from './videoViewReducer';

/** The toast shown when the call ends while full screen is on. */
export const CALL_ENDED_MESSAGE = 'The call ended, back to the island';

export function VideoViewProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(videoViewReducer, initialVideoViewState);
  const { room, isReconnecting, currentPrivateArea } = useVideoCall();
  const { addNotification } = useGameManager();

  const callIsLive = isCallLive({ room, isReconnecting, currentPrivateArea });

  // Read by `enterFullScreen`, which stays stable across renders.
  const callIsLiveRef = useRef(callIsLive);
  useEffect(() => {
    callIsLiveRef.current = callIsLive;
  }, [callIsLive]);

  const enterFullScreen = useCallback(() => {
    if (!callIsLiveRef.current) return;
    dispatch({ type: 'enter_fullscreen' });
  }, []);

  const exitFullScreen = useCallback(() => {
    dispatch({ type: 'exit_fullscreen' });
  }, []);

  const pin = useCallback((key: string) => {
    dispatch({ type: 'pin', key });
  }, []);

  const unpin = useCallback(() => {
    dispatch({ type: 'unpin' });
  }, []);

  const syncTiles = useCallback((keys: readonly string[], shareKeys: readonly string[]) => {
    dispatch({ type: 'tiles_changed', keys, shareKeys });
  }, []);

  // The call ended: back to the world with nothing pinned. Underneath full
  // screen, also say why.
  useEffect(() => {
    if (callIsLive) return;
    if (state.mode === 'fullscreen') {
      addNotification({ type: 'info', message: CALL_ENDED_MESSAGE });
    }
    dispatch({ type: 'call_ended' });
  }, [state.mode, callIsLive, addNotification]);

  const offeredShareKey = shareToOffer(state);

  const value = useMemo<VideoViewContextValue>(() => ({
    mode: state.mode,
    isFullScreen: state.mode === 'fullscreen',
    enterFullScreen,
    exitFullScreen,
    pinnedKey: state.pinnedKey,
    pin,
    unpin,
    offeredShareKey,
    syncTiles
  }), [state.mode, state.pinnedKey, offeredShareKey, enterFullScreen, exitFullScreen, pin, unpin, syncTiles]);

  return <VideoViewContext.Provider value={value}>{children}</VideoViewContext.Provider>;
}
